/**
 * Session type definitions matching backend schemas exactly.
 * These types ensure type safety and consistency with the API.
 */
import { z } from "zod"

// Base session properties matching SessionBase schema
export interface SessionBase {
  name: string
  slug: string
  description: string | null
  session_type:
    | "regular_season"
    | "summer_camp"
    | "winter_intensive"
    | "spring_break"
    | null
  start_week: string
  end_week: string
  registration_opens: string | null
  registration_closes: string | null
  early_bird_deadline: string | null
  makeup_classes_allowed: number | null
  total_classes: number
  no_class_dates: Record<string, unknown> | null
  is_active: boolean | null
  is_published: boolean | null
  notes: string | null
}

// Create session data matching SessionCreate schema
export interface SessionCreate extends SessionBase {}

// Update session data matching SessionUpdate schema
export interface SessionUpdate {
  name?: string
  slug?: string
  description?: string | null
  session_type?:
    | "regular_season"
    | "summer_camp"
    | "winter_intensive"
    | "spring_break"
    | null
  start_week?: string
  end_week?: string
  registration_opens?: string | null
  registration_closes?: string | null
  early_bird_deadline?: string | null
  makeup_classes_allowed?: number | null
  total_classes?: number
  no_class_dates?: Record<string, unknown> | null
  is_active?: boolean | null
  is_published?: boolean | null
  notes?: string | null
}

// Full session response matching SessionResponse schema
export interface SessionPublic extends SessionBase {
  id: string
  created_by: string
  created_at: string
  updated_by: string
  updated_at: string
}

// Response wrapper for API calls
export interface SessionResponse extends SessionPublic {}

// Paginated list response matching SessionListResponse schema
export interface SessionListResponse {
  items: SessionResponse[]
  total: number
  page: number
  per_page: number
  pages: number
  meta: SessionListMeta
}

export interface SessionListMeta {
  sort_by: "created_at" | "updated_at"
  sort_order: "asc" | "desc"
  filters: SessionFilters
}

// Form data interfaces for UI components
export interface SessionFormData {
  name?: string
  slug?: string
  description?: string | null
  session_type?:
    | "regular_season"
    | "summer_camp"
    | "winter_intensive"
    | "spring_break"
    | null
  start_week?: string
  end_week?: string
  registration_opens?: string | null
  registration_closes?: string | null
  early_bird_deadline?: string | null
  makeup_classes_allowed?: number | null
  total_classes?: number | null
  no_class_dates?: Record<string, unknown> | null
  is_active?: boolean | null
  is_published?: boolean | null
  notes?: string | null
}

export interface SessionUpdateFormData {
  name?: string
  slug?: string
  description?: string | null
  session_type?:
    | "regular_season"
    | "summer_camp"
    | "winter_intensive"
    | "spring_break"
    | null
  start_week?: string
  end_week?: string
  registration_opens?: string | null
  registration_closes?: string | null
  early_bird_deadline?: string | null
  makeup_classes_allowed?: number | null
  total_classes?: number | null
  no_class_dates?: Record<string, unknown> | null
  is_active?: boolean | null
  is_published?: boolean | null
  notes?: string | null
}

// Filter interface for API queries
export interface SessionFilters {
  limit?: number
  skip?: number
  search?: string
  sort_by?: "created_at" | "updated_at"
  sort_order?: "asc" | "desc"
  created_by?: string
  created_after?: string
  created_before?: string
  updated_after?: string
  updated_before?: string
}

// Component prop interfaces following sophisticated pattern
export interface AddSessionProps {
  onSuccess?: () => void
  onCancel?: () => void
}

export interface EditSessionProps {
  session: SessionResponse
  onSuccess?: () => void
  onCancel?: () => void
}

export interface DeleteSessionProps {
  session: SessionResponse
  onSuccess?: () => void
  onCancel?: () => void
}

// Zod validation schemas for forms
export const sessionCreateSchema = z.object({
  name: z.string().trim(),
  slug: z.string().trim(),
  description: z.string().trim().nullable().optional(),
  session_type: z
    .enum(["regular_season", "summer_camp", "winter_intensive", "spring_break"])
    .nullable()
    .optional(),
  start_week: z.string(),
  end_week: z.string(),
  registration_opens: z.string().nullable().optional(),
  registration_closes: z.string().nullable().optional(),
  early_bird_deadline: z.string().nullable().optional(),
  makeup_classes_allowed: z.coerce.number().int().nullable().optional(),
  total_classes: z.coerce.number().int().positive(),
  no_class_dates: z.any().nullable().optional(),
  is_active: z.boolean().nullable().optional(),
  is_published: z.boolean().nullable().optional(),
  notes: z.string().trim().nullable().optional(),
})

export const sessionUpdateSchema = z.object({
  name: z.string().trim().optional(),
  slug: z.string().trim().optional(),
  description: z.string().trim().nullable().optional(),
  session_type: z
    .enum(["regular_season", "summer_camp", "winter_intensive", "spring_break"])
    .nullable()
    .optional(),
  start_week: z.string().optional(),
  end_week: z.string().optional(),
  registration_opens: z.string().optional(),
  registration_closes: z.string().optional(),
  early_bird_deadline: z.string().nullable().optional(),
  makeup_classes_allowed: z.coerce.number().int().nullable().optional(),
  total_classes: z.coerce.number().int().positive().nullable().optional(),
  no_class_dates: z.any().nullable().optional(),
  is_active: z.boolean().nullable().optional(),
  is_published: z.boolean().nullable().optional(),
  notes: z.string().trim().nullable().optional(),
})

// Type inference from Zod schemas
export type SessionCreateInput = z.infer<typeof sessionCreateSchema>
export type SessionUpdateInput = z.infer<typeof sessionUpdateSchema>
